// src/Pages/CertificateAdmin.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import CertificatePreview from '../../Components/CertificatePreview';
import {
  CERTIFICATE_TYPES,
  PARTICIPANT_TYPES,
  getCertificateType,
  getParticipantType,
} from '../../config/certificateTemplates';
import './CertificateAdmin.css';

const CERTS_API = 'https://api.avtechbmt.cloud/api/certificates';

const DEBUG_DEFAULT = false;
function isDebugEnabled() {
  if (DEBUG_DEFAULT) return true;
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('certDebug') === '1';
  } catch {
    return false;
  }
}

const EMPTY_FORM = {
  certificateType: 'internship',
  participantType: 'student',

  recipientName: '',

  // student
  universityName: '',
  fieldName: '',

  // professional
  organizationName: '',
  designation: '',

  // common
  title: '',
  description: '',
  companyName: 'AV Tech Baramati Private Limited',
  location: '',
  startDate: '',
  endDate: '',
  duration: '',
  skills: '',
  authorizedSignatory: 'Authorized Signatory',
  signatoryTitle: 'Director',
  issueDate: new Date().toISOString().split('T')[0],
  internalNotes: '',
};

// Fields the backend allows in bulk updates (must match controller)
const BULK_ALLOWED = [
  'companyName',
  'authorizedSignatory',
  'signatoryTitle',
  'location',
  'internalNotes',
];

const EMPTY_BULK = {
  companyName: '',
  authorizedSignatory: '',
  signatoryTitle: '',
  location: '',
  internalNotes: '',
};

function toDateInputValue(value) {
  if (!value) return '';
  try {
    return new Date(value).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

function certToForm(cert) {
  return {
    certificateType: cert.certificateType || 'internship',
    participantType: cert.participantType || 'student',

    recipientName: cert.recipientName || '',

    universityName: cert.universityName || '',
    fieldName: cert.fieldName || '',

    organizationName: cert.organizationName || '',
    designation: cert.designation || '',

    title: cert.title || '',
    description: cert.description || '',
    companyName: cert.companyName || '',
    location: cert.location || '',
    startDate: toDateInputValue(cert.startDate),
    endDate: toDateInputValue(cert.endDate),
    duration: cert.duration || '',
    skills: cert.skills || '',
    authorizedSignatory: cert.authorizedSignatory || '',
    signatoryTitle: cert.signatoryTitle || '',
    issueDate: toDateInputValue(cert.issueDate),
    internalNotes: cert.internalNotes || '',
  };
}

export default function CertificateAdmin() {
  const { token } = useAuth();

  const [tab, setTab] = useState('issue');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [certificates, setCertificates] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [participantFilter, setParticipantFilter] = useState('all');
  const [certTypeFilter, setCertTypeFilter] = useState('all');

  const [previewCert, setPreviewCert] = useState(null);

  // Edit modal state
  const [editingCert, setEditingCert] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  // Bulk state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkForm, setBulkForm] = useState(EMPTY_BULK);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const [debug, setDebug] = useState(isDebugEnabled);
  const [debugLog, setDebugLog] = useState([]);
  const [lastRequest, setLastRequest] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);

  const log = useCallback((level, message, extra) => {
    setDebugLog((prev) =>
      [
        {
          id: Date.now() + Math.random(),
          ts: new Date().toLocaleTimeString('en-IN'),
          level,
          message,
          extra,
        },
        ...prev,
      ].slice(0, 50)
    );
  }, []);

  const clearLog = () => setDebugLog([]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        setDebug((d) => !d);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!debug) return;
    log('info', 'Debug panel enabled', { shortcut: 'Ctrl+Shift+D' });
    log('info', `API base: ${CERTS_API}`);
    log('info', `Auth token: ${token ? 'present' : 'MISSING'}`);
  }, [debug, token, log]);

  const getHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token')}`,
    }),
    [token]
  );

  /* ---------------------------------------------------------------------- */
  /* Generic fetch helper with JSON handling + debug logging                */
  /* ---------------------------------------------------------------------- */
  const apiFetch = useCallback(
    async (method, url, body) => {
      if (debug) {
        setLastRequest({
          method,
          url,
          body: body ?? null,
          ts: new Date().toLocaleTimeString('en-IN'),
        });
        log('info', `${method} ${url}`);
      }

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      const raw = await res.text();
      let data;
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        const isHtml = raw.trim().startsWith('<');
        const msg = isHtml
          ? `Server returned HTML instead of JSON (HTTP ${res.status}). Verify API endpoint route.`
          : `Non-JSON response (HTTP ${res.status}).`;

        if (debug) {
          setLastResponse({
            status: res.status,
            ok: res.ok,
            raw: raw.slice(0, 500),
          });
          log('error', msg);
        }
        throw new Error(msg);
      }

      if (debug) {
        setLastResponse({
          status: res.status,
          ok: res.ok,
          raw: raw.slice(0, 500),
          parsed: data,
        });
        log(
          res.ok ? 'ok' : 'error',
          `HTTP ${res.status} · ${data.message ?? data.count ?? ''}`
        );
      }

      if (!res.ok || data.success === false) {
        throw new Error(data.message || `Request failed (HTTP ${res.status}).`);
      }
      return data;
    },
    [debug, getHeaders, log]
  );

  /* ---------------------------------------------------------------------- */
  const fetchCertificates = useCallback(async () => {
    setListLoading(true);
    setError('');

    const qs = new URLSearchParams();
    if (search) qs.append('search', search);
    if (statusFilter !== 'all') qs.append('status', statusFilter);
    if (participantFilter !== 'all')
      qs.append('participantType', participantFilter);
    if (certTypeFilter !== 'all')
      qs.append('certificateType', certTypeFilter);

    const url = `${CERTS_API}?${qs.toString()}`;

    try {
      const data = await apiFetch('GET', url);
      setCertificates(data.data || []);
    } catch (err) {
      if (!debug) setError(err.message || 'Failed to load certificates.');
      else setError(err.message || 'Failed to load certificates.');
    } finally {
      setListLoading(false);
    }
  }, [search, statusFilter, participantFilter, certTypeFilter, apiFetch, debug]);

  useEffect(() => {
    if (tab === 'list') fetchCertificates();
  }, [tab, fetchCertificates]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(t);
  }, [error]);

  /* ---------------------------------------------------------------------- */
  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () => {
    const ct = getCertificateType(form.certificateType);
    setForm({
      ...EMPTY_FORM,
      certificateType: form.certificateType,
      title: ct.defaultTitle,
    });
  };

  const changeCertificateType = (id) => {
    const ct = getCertificateType(id);
    setForm((f) => ({
      ...f,
      certificateType: id,
      title:
        f.title && f.title !== getCertificateType(f.certificateType).defaultTitle
          ? f.title
          : ct.defaultTitle,
    }));
  };

  /* ---------------------------------------------------------------------- */
  const handleCreate = async (e) => {
    e?.preventDefault?.();
    setError('');
    setSuccess('');

    if (!form.recipientName.trim()) return setError('Recipient name is required.');

    const ct = getCertificateType(form.certificateType);
    if (ct.requiresStartDate && !form.startDate)
      return setError('Start date is required for this certificate type.');
    if (ct.requiresEndDate && !form.endDate)
      return setError('End date is required for this certificate type.');
    if (
      form.startDate &&
      form.endDate &&
      new Date(form.endDate) < new Date(form.startDate)
    )
      return setError('End date cannot be before start date.');

    setSaving(true);

    const isProfessional = form.participantType === 'professional';

    const payload = {
      certificateType: form.certificateType,
      participantType: isProfessional ? 'professional' : 'student',
      recipientName: form.recipientName.trim(),

      universityName: isProfessional ? '' : (form.universityName || '').trim(),
      fieldName: isProfessional ? '' : (form.fieldName || '').trim(),

      organizationName: isProfessional ? (form.organizationName || '').trim() : '',
      designation: isProfessional ? (form.designation || '').trim() : '',

      title: (form.title || '').trim(),
      description: (form.description || '').trim(),
      companyName:
        (form.companyName || '').trim() || 'AV Tech Baramati Private Limited',
      location: (form.location || '').trim(),

      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
      duration: (form.duration || '').trim(),
      skills: (form.skills || '').trim(),

      authorizedSignatory:
        (form.authorizedSignatory || '').trim() || 'Authorized Signatory',
      signatoryTitle: (form.signatoryTitle || '').trim(),
      issueDate: form.issueDate || undefined,
      internalNotes: (form.internalNotes || '').trim(),
    };

    try {
      const data = await apiFetch('POST', CERTS_API, payload);
      setSuccess(`✅ Certificate ${data.data.certificateId} created!`);
      setPreviewCert(data.data);
      resetForm();
    } catch (err) {
      setError(err.message || 'Failed to create certificate.');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  const handleDelete = async (cert) => {
    if (
      !window.confirm(
        `Delete certificate ${cert.certificateId} for ${cert.recipientName}?`
      )
    )
      return;

    try {
      await apiFetch('DELETE', `${CERTS_API}/${cert._id}`);
      setSuccess('Certificate deleted.');
      setCertificates((prev) => prev.filter((c) => c._id !== cert._id));
      setSelectedIds((prev) => prev.filter((id) => id !== cert._id));
    } catch (err) {
      setError(err.message || 'Failed to delete certificate.');
    }
  };

  /* ---------------------------------------------------------------------- */
  const handleRevoke = async (cert) => {
    if (!window.confirm(`Revoke certificate ${cert.certificateId}?`)) return;
    try {
      await apiFetch('PATCH', `${CERTS_API}/${cert._id}/revoke`);
      setSuccess('Certificate revoked.');
      fetchCertificates();
    } catch (err) {
      setError(err.message || 'Failed to revoke certificate.');
    }
  };

  const handleRestore = async (cert) => {
    if (!window.confirm(`Restore certificate ${cert.certificateId}?`)) return;
    try {
      await apiFetch('PATCH', `${CERTS_API}/${cert._id}/restore`);
      setSuccess('Certificate restored.');
      fetchCertificates();
    } catch (err) {
      setError(err.message || 'Failed to restore certificate.');
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Edit modal                                                             */
  /* ---------------------------------------------------------------------- */
  const openEdit = (cert) => {
    if (cert.status === 'revoked') {
      setError('Revoked certificates cannot be edited. Restore it first.');
      return;
    }
    setEditingCert(cert);
    setEditForm(certToForm(cert));
  };

  const closeEdit = () => {
    setEditingCert(null);
    setEditForm(null);
  };

  const updateEditField = (k, v) =>
    setEditForm((f) => (f ? { ...f, [k]: v } : f));

  const saveEdit = async () => {
    if (!editingCert || !editForm) return;
    setError('');
    setSuccess('');

    const ct = getCertificateType(editForm.certificateType);
    if (!editForm.recipientName.trim())
      return setError('Recipient name is required.');
    if (ct.requiresStartDate && !editForm.startDate)
      return setError('Start date is required for this certificate type.');
    if (ct.requiresEndDate && !editForm.endDate)
      return setError('End date is required for this certificate type.');
    if (
      editForm.startDate &&
      editForm.endDate &&
      new Date(editForm.endDate) < new Date(editForm.startDate)
    )
      return setError('End date cannot be before start date.');

    setEditSaving(true);

    const isProfessional = editForm.participantType === 'professional';

    const payload = {
      certificateType: editForm.certificateType,
      participantType: isProfessional ? 'professional' : 'student',
      recipientName: editForm.recipientName.trim(),

      universityName: isProfessional ? '' : (editForm.universityName || '').trim(),
      fieldName: isProfessional ? '' : (editForm.fieldName || '').trim(),
      organizationName: isProfessional
        ? (editForm.organizationName || '').trim()
        : '',
      designation: isProfessional ? (editForm.designation || '').trim() : '',

      title: (editForm.title || '').trim(),
      description: (editForm.description || '').trim(),
      companyName: (editForm.companyName || '').trim(),
      location: (editForm.location || '').trim(),
      startDate: editForm.startDate || undefined,
      endDate: editForm.endDate || undefined,
      duration: (editForm.duration || '').trim(),
      skills: (editForm.skills || '').trim(),
      authorizedSignatory: (editForm.authorizedSignatory || '').trim(),
      signatoryTitle: (editForm.signatoryTitle || '').trim(),
      issueDate: editForm.issueDate || undefined,
      internalNotes: (editForm.internalNotes || '').trim(),
    };

    try {
      const data = await apiFetch(
        'PUT',
        `${CERTS_API}/${editingCert._id}`,
        payload
      );
      setSuccess(`Certificate ${data.data.certificateId} updated.`);
      closeEdit();
      fetchCertificates();
    } catch (err) {
      setError(err.message || 'Failed to update certificate.');
    } finally {
      setEditSaving(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Bulk update                                                            */
  /* ---------------------------------------------------------------------- */
  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const editable = certificates
      .filter((c) => c.status !== 'revoked')
      .map((c) => c._id);
    if (selectedIds.length === editable.length && editable.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(editable);
    }
  };

  const submitBulk = async () => {
    if (selectedIds.length === 0) {
      setError('Select at least one certificate.');
      return;
    }
    const cleanUpdates = {};
    BULK_ALLOWED.forEach((k) => {
      if (bulkForm[k] !== undefined && bulkForm[k].trim() !== '') {
        cleanUpdates[k] = bulkForm[k].trim();
      }
    });
    if (Object.keys(cleanUpdates).length === 0) {
      setError('Provide at least one field to update.');
      return;
    }

    setBulkSaving(true);
    setError('');
    try {
      const data = await apiFetch('PATCH', `${CERTS_API}/bulk/update`, {
        ids: selectedIds,
        updates: cleanUpdates,
      });
      setSuccess(data.message || `Updated ${selectedIds.length} certificate(s).`);
      setSelectedIds([]);
      setBulkForm(EMPTY_BULK);
      setBulkOpen(false);
      fetchCertificates();
    } catch (err) {
      setError(err.message || 'Bulk update failed.');
    } finally {
      setBulkSaving(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  const isProfessional = form.participantType === 'professional';
  const currentType = getCertificateType(form.certificateType);

  const allEditableSelected = useMemo(() => {
    const editable = certificates.filter((c) => c.status !== 'revoked');
    return (
      editable.length > 0 &&
      selectedIds.length === editable.length &&
      editable.every((c) => selectedIds.includes(c._id))
    );
  }, [certificates, selectedIds]);

  /* ---------------------------------------------------------------------- */
  return (
    <div className="cert-admin-container">
      {/* HEADER */}
      <div className="cert-admin-header">
        <div>
          <span className="cert-admin-eyebrow">06 / Credentials Studio</span>
          <h2>Certificate Management</h2>
          <p className="cert-admin-sub">
            Issue, preview, edit, and download any certificate type —
            internships, workshops, courses, training, appreciation and more.
          </p>
        </div>

        <div className="cert-admin-tabs">
          <button
            className={`cert-tab ${tab === 'issue' ? 'is-active' : ''}`}
            onClick={() => setTab('issue')}
          >
            ＋ Issue New
          </button>
          <button
            className={`cert-tab ${tab === 'list' ? 'is-active' : ''}`}
            onClick={() => setTab('list')}
          >
            📜 All Certificates
          </button>
          <button
            className={`cert-tab ${debug ? 'is-active' : ''}`}
            onClick={() => setDebug((d) => !d)}
          >
            🐞 Debug {debug ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* DEBUG PANEL */}
      {debug && (
        <div className="cert-debug-panel">
          <div className="cert-debug-header">
            <span>🐞 Certificate Admin Debug</span>
            <div className="cert-debug-actions">
              <button className="cert-debug-btn" onClick={clearLog} type="button">
                🧹 Clear log
              </button>
              <button
                className="cert-debug-btn"
                onClick={() => setDebug(false)}
                type="button"
              >
                ✕ Close
              </button>
            </div>
          </div>
          <div className="cert-debug-section">
            <span className="cert-debug-section-label">Last request</span>
            {lastRequest ? (
              <pre className="cert-debug-pre">
                {`[${lastRequest.ts}] ${lastRequest.method} ${lastRequest.url}`}
                {lastRequest.body
                  ? `\n\nPayload:\n${JSON.stringify(lastRequest.body, null, 2)}`
                  : ''}
              </pre>
            ) : (
              <p className="cert-debug-hint">No request yet.</p>
            )}
          </div>
          <div className="cert-debug-section">
            <span className="cert-debug-section-label">Last response</span>
            {lastResponse ? (
              <pre className="cert-debug-pre">
                {lastResponse.parsed
                  ? JSON.stringify(lastResponse.parsed, null, 2)
                  : lastResponse.raw || '(empty)'}
              </pre>
            ) : (
              <p className="cert-debug-hint">No response yet.</p>
            )}
          </div>
          <div className="cert-debug-section">
            <span className="cert-debug-section-label">
              Event log ({debugLog.length})
            </span>
            <ul className="cert-debug-log">
              {debugLog.map((entry) => (
                <li key={entry.id} className={`cert-debug-log-${entry.level}`}>
                  <span className="cert-debug-log-ts">[{entry.ts}]</span>{' '}
                  {entry.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {error && <div className="cert-alert cert-alert-error">⚠️ {error}</div>}
      {success && <div className="cert-alert cert-alert-success">{success}</div>}

      {/* ============================================================ */}
      {/* ISSUE TAB                                                    */}
      {/* ============================================================ */}
      {tab === 'issue' && (
        <div className="cert-issue-layout">
          <form className="cert-form-card" onSubmit={handleCreate}>
            {/* 1. CERTIFICATE TYPE */}
            <h3 className="cert-section-title">1️⃣ Certificate Type</h3>
            <div className="cert-type-grid">
              {CERTIFICATE_TYPES.map((ct) => (
                <button
                  key={ct.id}
                  type="button"
                  className={`cert-type-card ${
                    form.certificateType === ct.id ? 'is-active' : ''
                  }`}
                  style={{ '--accent': ct.tone }}
                  onClick={() => changeCertificateType(ct.id)}
                >
                  <span className="cert-type-icon">{ct.icon}</span>
                  <strong>{ct.label}</strong>
                  <span className="cert-type-desc">{ct.description}</span>
                </button>
              ))}
            </div>

            {/* 2. PARTICIPANT TYPE */}
            <h3 className="cert-section-title" style={{ marginTop: 24 }}>
              2️⃣ Recipient Type
            </h3>
            <div className="cert-participant-toggle">
              {PARTICIPANT_TYPES.map((pt) => (
                <button
                  key={pt.id}
                  type="button"
                  className={`cert-toggle-btn ${
                    form.participantType === pt.id ? 'is-active' : ''
                  }`}
                  onClick={() =>
                    setForm((f) => ({ ...f, participantType: pt.id }))
                  }
                >
                  <span className="cert-toggle-icon">{pt.icon}</span>
                  <div className="cert-toggle-body">
                    <strong>{pt.label}</strong>
                    <span>{pt.hint}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* 3. RECIPIENT DETAILS */}
            <h3 className="cert-section-title" style={{ marginTop: 24 }}>
              3️⃣ Recipient Details
            </h3>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Recipient Name *</label>
                <input
                  type="text"
                  value={form.recipientName}
                  onChange={(e) => updateField('recipientName', e.target.value)}
                  placeholder="e.g. Aniket Wable"
                  required
                />
              </div>

              {!isProfessional ? (
                <div className="cert-field">
                  <label>University / College</label>
                  <input
                    type="text"
                    value={form.universityName}
                    onChange={(e) => updateField('universityName', e.target.value)}
                    placeholder="e.g. Savitribai Phule Pune University"
                  />
                </div>
              ) : (
                <div className="cert-field">
                  <label>Organisation</label>
                  <input
                    type="text"
                    value={form.organizationName}
                    onChange={(e) => updateField('organizationName', e.target.value)}
                    placeholder="e.g. TCS / Freelance / Self-employed"
                  />
                </div>
              )}
            </div>

            <div className="cert-grid-2">
              {!isProfessional ? (
                <div className="cert-field">
                  <label>Field of Study / Department</label>
                  <input
                    type="text"
                    value={form.fieldName}
                    onChange={(e) => updateField('fieldName', e.target.value)}
                    placeholder="e.g. Computer Science"
                  />
                </div>
              ) : (
                <div className="cert-field">
                  <label>Designation</label>
                  <input
                    type="text"
                    value={form.designation}
                    onChange={(e) => updateField('designation', e.target.value)}
                    placeholder="e.g. Senior Software Engineer"
                  />
                </div>
              )}

              <div className="cert-field">
                <label>Company (issuing)</label>
                <input
                  type="text"
                  value={form.companyName}
                  onChange={(e) => updateField('companyName', e.target.value)}
                />
              </div>
            </div>

            {/* 4. PROGRAM DETAILS */}
            <h3 className="cert-section-title" style={{ marginTop: 24 }}>
              4️⃣ {currentType.label} Details
            </h3>

            <div className="cert-field">
              <label>Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder={currentType.defaultTitle}
              />
            </div>

            {(form.certificateType === 'appreciation' ||
              form.certificateType === 'completion') && (
              <div className="cert-field">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={form.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="e.g. In recognition of outstanding contribution…"
                />
              </div>
            )}

            <div className="cert-grid-2">
              {currentType.requiresStartDate && (
                <div className="cert-field">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => updateField('startDate', e.target.value)}
                  />
                </div>
              )}

              {currentType.requiresEndDate && (
                <div className="cert-field">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={(e) => updateField('endDate', e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Duration</label>
                <input
                  type="text"
                  value={form.duration}
                  onChange={(e) => updateField('duration', e.target.value)}
                  placeholder="e.g. 3 Months / 2 Days / 40 Hours"
                />
              </div>
              <div className="cert-field">
                <label>Location / Mode</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  placeholder="e.g. Online / Pune / Hybrid"
                />
              </div>
            </div>

            <div className="cert-field">
              <label>Skills / Topics / Competencies</label>
              <textarea
                rows="3"
                value={form.skills}
                onChange={(e) => updateField('skills', e.target.value)}
                placeholder="e.g. React, Node.js, MongoDB, REST APIs"
              />
            </div>

            {/* 5. SIGNATORY */}
            <h3 className="cert-section-title" style={{ marginTop: 24 }}>
              5️⃣ Signatory &amp; Meta
            </h3>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Authorized Signatory</label>
                <input
                  type="text"
                  value={form.authorizedSignatory}
                  onChange={(e) =>
                    updateField('authorizedSignatory', e.target.value)
                  }
                />
              </div>
              <div className="cert-field">
                <label>Signatory Title</label>
                <input
                  type="text"
                  value={form.signatoryTitle}
                  onChange={(e) => updateField('signatoryTitle', e.target.value)}
                />
              </div>
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Issue Date</label>
                <input
                  type="date"
                  value={form.issueDate}
                  onChange={(e) => updateField('issueDate', e.target.value)}
                />
              </div>
            </div>

            <div className="cert-field">
              <label>Internal Notes (not shown)</label>
              <textarea
                rows="2"
                value={form.internalNotes}
                onChange={(e) => updateField('internalNotes', e.target.value)}
              />
            </div>

            <div className="cert-form-actions">
              <button
                type="button"
                className="cert-btn secondary"
                onClick={resetForm}
                disabled={saving}
              >
                Reset
              </button>
              <button type="submit" className="cert-btn primary" disabled={saving}>
                {saving ? '⏳ Creating…' : '✓ Issue Certificate'}
              </button>
            </div>
          </form>

          <div className="cert-preview-card">
            <h3 className="cert-section-title">👁 Live Preview</h3>
            <CertificatePreview
              data={{
                ...form,
                certificateId: 'PREVIEW',
                verificationToken: 'preview',
              }}
              mode="form"
              printId="printable-certificate-form"
            />
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* LIST TAB                                                     */}
      {/* ============================================================ */}
      {tab === 'list' && (
        <div className="cert-list-wrapper">
          <div className="cert-list-toolbar">
            <input
              type="text"
              placeholder="🔍 Search by name, ID, university, org, title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="cert-search"
            />
            <select
              value={certTypeFilter}
              onChange={(e) => setCertTypeFilter(e.target.value)}
              className="cert-select"
            >
              <option value="all">All Certificate Types</option>
              {CERTIFICATE_TYPES.map((ct) => (
                <option key={ct.id} value={ct.id}>
                  {ct.icon} {ct.label}
                </option>
              ))}
            </select>
            <select
              value={participantFilter}
              onChange={(e) => setParticipantFilter(e.target.value)}
              className="cert-select"
            >
              <option value="all">All Recipient Types</option>
              {PARTICIPANT_TYPES.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.icon} {pt.label}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="cert-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="revoked">Revoked</option>
            </select>
            <button
              className="cert-btn secondary"
              onClick={fetchCertificates}
              disabled={listLoading}
            >
              ↻ Refresh
            </button>
            <button
              className="cert-btn secondary"
              onClick={() => setBulkOpen((v) => !v)}
              disabled={selectedIds.length === 0}
              title={
                selectedIds.length === 0
                  ? 'Select certificates below to bulk update'
                  : 'Bulk update selected'
              }
            >
              ⚡ Bulk Update ({selectedIds.length})
            </button>
          </div>

          {/* BULK PANEL */}
          {bulkOpen && selectedIds.length > 0 && (
            <div className="cert-bulk-panel">
              <div className="cert-bulk-header">
                <strong>⚡ Bulk update {selectedIds.length} certificate(s)</strong>
                <span className="cert-bulk-hint">
                  Only non-empty fields are applied. Revoked certificates are
                  excluded.
                </span>
              </div>
              <div className="cert-grid-2">
                <div className="cert-field">
                  <label>Company Name</label>
                  <input
                    type="text"
                    value={bulkForm.companyName}
                    onChange={(e) =>
                      setBulkForm((f) => ({ ...f, companyName: e.target.value }))
                    }
                    placeholder="Leave blank to keep existing"
                  />
                </div>
                <div className="cert-field">
                  <label>Authorized Signatory</label>
                  <input
                    type="text"
                    value={bulkForm.authorizedSignatory}
                    onChange={(e) =>
                      setBulkForm((f) => ({
                        ...f,
                        authorizedSignatory: e.target.value,
                      }))
                    }
                    placeholder="Leave blank to keep existing"
                  />
                </div>
                <div className="cert-field">
                  <label>Signatory Title</label>
                  <input
                    type="text"
                    value={bulkForm.signatoryTitle}
                    onChange={(e) =>
                      setBulkForm((f) => ({
                        ...f,
                        signatoryTitle: e.target.value,
                      }))
                    }
                    placeholder="Leave blank to keep existing"
                  />
                </div>
                <div className="cert-field">
                  <label>Location / Mode</label>
                  <input
                    type="text"
                    value={bulkForm.location}
                    onChange={(e) =>
                      setBulkForm((f) => ({ ...f, location: e.target.value }))
                    }
                    placeholder="Leave blank to keep existing"
                  />
                </div>
              </div>
              <div className="cert-field">
                <label>Internal Notes</label>
                <textarea
                  rows="2"
                  value={bulkForm.internalNotes}
                  onChange={(e) =>
                    setBulkForm((f) => ({ ...f, internalNotes: e.target.value }))
                  }
                  placeholder="Leave blank to keep existing"
                />
              </div>
              <div className="cert-form-actions">
                <button
                  className="cert-btn secondary"
                  onClick={() => {
                    setBulkOpen(false);
                    setBulkForm(EMPTY_BULK);
                  }}
                  disabled={bulkSaving}
                >
                  Cancel
                </button>
                <button
                  className="cert-btn primary"
                  onClick={submitBulk}
                  disabled={bulkSaving}
                >
                  {bulkSaving ? '⏳ Applying…' : '✓ Apply to Selected'}
                </button>
              </div>
            </div>
          )}

          {listLoading ? (
            <div className="cert-empty">Loading certificates…</div>
          ) : certificates.length === 0 ? (
            <div className="cert-empty">
              <span className="icon">📜</span>
              <span>No certificates issued yet.</span>
            </div>
          ) : (
            <div className="cert-table-wrapper">
              <table className="cert-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={allEditableSelected}
                        onChange={toggleSelectAll}
                        title="Select all editable (non-revoked)"
                      />
                    </th>
                    <th>Certificate ID</th>
                    <th>Recipient</th>
                    <th>Certificate Type</th>
                    <th>Recipient Type</th>
                    <th>Title</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates.map((c) => {
                    const pType = c.participantType || 'student';
                    const ct = getCertificateType(c.certificateType);
                    const isRevoked = c.status === 'revoked';
                    return (
                      <tr key={c._id}>
                        <td>
                          <input
                            type="checkbox"
                            disabled={isRevoked}
                            checked={selectedIds.includes(c._id)}
                            onChange={() => toggleSelected(c._id)}
                            title={
                              isRevoked
                                ? 'Revoked certificates cannot be bulk updated'
                                : 'Select'
                            }
                          />
                        </td>
                        <td className="cert-id-cell">{c.certificateId}</td>
                        <td>{c.recipientName}</td>
                        <td>
                          <span className="cert-ct-pill">
                            {ct.icon} {ct.label}
                          </span>
                        </td>
                        <td>
                          <span className={`cert-type-pill is-${pType}`}>
                            {pType === 'professional'
                              ? '💼 Non-Student'
                              : '🎓 Student'}
                          </span>
                        </td>
                        <td>{c.title || c.fieldName || '—'}</td>
                        <td>
                          {c.startDate
                            ? new Date(c.startDate).toLocaleDateString('en-IN', {
                                month: 'short',
                                year: 'numeric',
                              })
                            : '—'}
                          {c.endDate
                            ? ` → ${new Date(c.endDate).toLocaleDateString(
                                'en-IN',
                                { month: 'short', year: 'numeric' }
                              )}`
                            : ''}
                        </td>
                        <td>
                          <span className={`cert-status-pill is-${c.status}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="cert-actions-cell">
                          <button
                            className="cert-icon-btn"
                            title="Preview"
                            onClick={() => setPreviewCert(c)}
                          >
                            👁
                          </button>
                          <button
                            className="cert-icon-btn"
                            title={
                              isRevoked
                                ? 'Restore first to edit'
                                : 'Edit certificate'
                            }
                            onClick={() => openEdit(c)}
                            disabled={isRevoked}
                          >
                            ✏️
                          </button>
                          {isRevoked ? (
                            <button
                              className="cert-icon-btn"
                              title="Restore"
                              onClick={() => handleRestore(c)}
                            >
                              ♻
                            </button>
                          ) : (
                            <button
                              className="cert-icon-btn"
                              title="Revoke"
                              onClick={() => handleRevoke(c)}
                            >
                              🚫
                            </button>
                          )}
                          <button
                            className="cert-icon-btn danger"
                            title="Delete"
                            onClick={() => handleDelete(c)}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* PREVIEW MODAL */}
      {previewCert && (
        <div
          className="cert-modal-overlay"
          onClick={() => setPreviewCert(null)}
        >
          <div className="cert-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="cert-modal-close"
              onClick={() => setPreviewCert(null)}
            >
              ✕
            </button>
            <CertificatePreview
              data={previewCert}
              mode="saved"
              printId="printable-certificate"
            />
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editingCert && editForm && (
        <div className="cert-modal-overlay" onClick={closeEdit}>
          <div
            className="cert-modal cert-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="cert-modal-close" onClick={closeEdit}>
              ✕
            </button>
            <h3 className="cert-section-title" style={{ marginTop: 0 }}>
              ✏️ Edit Certificate {editingCert.certificateId}
            </h3>
            <p className="cert-admin-sub" style={{ marginTop: 0 }}>
              Updating: {editingCert.recipientName}
            </p>

            {/* Certificate type */}
            <div className="cert-field">
              <label>Certificate Type</label>
              <select
                value={editForm.certificateType}
                onChange={(e) => updateEditField('certificateType', e.target.value)}
              >
                {CERTIFICATE_TYPES.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.icon} {ct.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Participant type */}
            <div className="cert-field">
              <label>Recipient Type</label>
              <select
                value={editForm.participantType}
                onChange={(e) =>
                  updateEditField('participantType', e.target.value)
                }
              >
                {PARTICIPANT_TYPES.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.icon} {pt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Recipient Name *</label>
                <input
                  type="text"
                  value={editForm.recipientName}
                  onChange={(e) => updateEditField('recipientName', e.target.value)}
                />
              </div>

              {editForm.participantType === 'professional' ? (
                <div className="cert-field">
                  <label>Organisation</label>
                  <input
                    type="text"
                    value={editForm.organizationName}
                    onChange={(e) =>
                      updateEditField('organizationName', e.target.value)
                    }
                  />
                </div>
              ) : (
                <div className="cert-field">
                  <label>University / College</label>
                  <input
                    type="text"
                    value={editForm.universityName}
                    onChange={(e) =>
                      updateEditField('universityName', e.target.value)
                    }
                  />
                </div>
              )}
            </div>

            <div className="cert-grid-2">
              {editForm.participantType === 'professional' ? (
                <div className="cert-field">
                  <label>Designation</label>
                  <input
                    type="text"
                    value={editForm.designation}
                    onChange={(e) =>
                      updateEditField('designation', e.target.value)
                    }
                  />
                </div>
              ) : (
                <div className="cert-field">
                  <label>Field of Study</label>
                  <input
                    type="text"
                    value={editForm.fieldName}
                    onChange={(e) => updateEditField('fieldName', e.target.value)}
                  />
                </div>
              )}

              <div className="cert-field">
                <label>Company (issuing)</label>
                <input
                  type="text"
                  value={editForm.companyName}
                  onChange={(e) =>
                    updateEditField('companyName', e.target.value)
                  }
                />
              </div>
            </div>

            <div className="cert-field">
              <label>Title</label>
              <input
                type="text"
                value={editForm.title}
                onChange={(e) => updateEditField('title', e.target.value)}
              />
            </div>

            <div className="cert-field">
              <label>Description</label>
              <textarea
                rows="2"
                value={editForm.description}
                onChange={(e) => updateEditField('description', e.target.value)}
              />
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={editForm.startDate}
                  onChange={(e) => updateEditField('startDate', e.target.value)}
                />
              </div>
              <div className="cert-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={editForm.endDate}
                  onChange={(e) => updateEditField('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Duration</label>
                <input
                  type="text"
                  value={editForm.duration}
                  onChange={(e) => updateEditField('duration', e.target.value)}
                />
              </div>
              <div className="cert-field">
                <label>Location / Mode</label>
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => updateEditField('location', e.target.value)}
                />
              </div>
            </div>

            <div className="cert-field">
              <label>Skills / Topics</label>
              <textarea
                rows="2"
                value={editForm.skills}
                onChange={(e) => updateEditField('skills', e.target.value)}
              />
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Authorized Signatory</label>
                <input
                  type="text"
                  value={editForm.authorizedSignatory}
                  onChange={(e) =>
                    updateEditField('authorizedSignatory', e.target.value)
                  }
                />
              </div>
              <div className="cert-field">
                <label>Signatory Title</label>
                <input
                  type="text"
                  value={editForm.signatoryTitle}
                  onChange={(e) =>
                    updateEditField('signatoryTitle', e.target.value)
                  }
                />
              </div>
            </div>

            <div className="cert-grid-2">
              <div className="cert-field">
                <label>Issue Date</label>
                <input
                  type="date"
                  value={editForm.issueDate}
                  onChange={(e) => updateEditField('issueDate', e.target.value)}
                />
              </div>
            </div>

            <div className="cert-field">
              <label>Internal Notes</label>
              <textarea
                rows="2"
                value={editForm.internalNotes}
                onChange={(e) =>
                  updateEditField('internalNotes', e.target.value)
                }
              />
            </div>

            <div className="cert-form-actions">
              <button
                type="button"
                className="cert-btn secondary"
                onClick={closeEdit}
                disabled={editSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cert-btn primary"
                onClick={saveEdit}
                disabled={editSaving}
              >
                {editSaving ? '⏳ Saving…' : '✓ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}